import Controller, { OnMount, OnReadSuccess } from '@philips-internal/oswald-ui-core/lib/client';
import { BarcodeAPI, BarcodeData, MapperAPI, PanelAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { MountEvent, ReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import { Value } from '@philips-internal/oswald-ui-core';

import BaseController from '../../BaseController';
import { FragmentNames, PatientRoomCheckAliases } from '../../../codegen';
import { Endpoint } from '../../../constants/endpoints';
import { YesNoType } from '../../../utils/enum-utils';
import { BarcodeExpressions, ConsistSurgicalPatientCheckResponse, DisplayMessages, PatientRoomInfoResponse, SugicalRoomCkecResponse } from './patient-room-check-types';
import { FoundationUtils } from '../../../utils/foundation-utils';
import { FunctionName, SURGICAL_9139Parameters } from '../../../utils/parameters';
import BarcodeService from '../../../services/barcode-service';
import { isEmpty } from 'lodash-es';
import './patient-room-check.css';


@Controller ({ component: PatientRoomCheckAliases.PATIENT_ROOM_CHECK_LAYOUT, fragment: FragmentNames.PATIENT_ROOM_CHECK })
export default class HealthHistoryAllergiesListController extends BaseController {

  @Inject(PatientRoomCheckAliases.PATIENT_ROOM_CHECK_CARD_PANEL)
  private cardPanelAPI!: PanelAPI;

  @Inject(PatientRoomCheckAliases.PATIENT_ROOM_CHECK_BARCODE)
  private barcodeAPI!: BarcodeAPI;

  private firstCheck!: boolean;

  @OnMount(PatientRoomCheckAliases.PATIENT_ROOM_CHECK_LAYOUT)
  handleMountPatientRoomCheckLayout(): void {
    this.firstCheck = true;
  }

  @OnMount(PatientRoomCheckAliases.PATIENT_ROOM_CHECK_MAPPER)
  handleMountPatientRoomCheckMapper(event: MountEvent): void {
    const mapperApi = event.target as MapperAPI;
    const patientRoomInfo = this.context.get('patientRoomInfo') as Record<string, Value>;
    mapperApi.setData( patientRoomInfo );
  }

  @OnMount(PatientRoomCheckAliases.PATIENT_ROOM_CHECK_BARCODE)
  handleMountPatientRoomCheckBarcode(): void {
    this.barcodeAPI.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode());
  }

  @OnReadSuccess({ target: PatientRoomCheckAliases.PATIENT_ROOM_CHECK_BARCODE })
  readSuccess(event: ReadEvent<BarcodeData>):void {
    const barCode = event.item.value.trim();
    const contextKey = this.firstCheck ? 'encounterId' : 'roomId';
    this.context.set({ [contextKey]: barCode });
    this.cardPanelAPI.setVisible(false);

    if (this.firstCheck) {
      this.handlePatientRoomCheck();
    } else {
      this.handleSurgicalRoomCheck();
    }
  }

  private handlePatientRoomCheck(): void {
    const consistSurgicalPatientCheckParam = FoundationUtils.findModuleParam(FunctionName.SURGICAL_9139, SURGICAL_9139Parameters.CONSIST_SURGICAL_PATIENT_CHECK) === YesNoType.YES;

    if (consistSurgicalPatientCheckParam) {
      this.handleConsistSurgicalPatientCheck();
    } else {
      this.handlePatientRoomInfo();
    }
  }

  private handleConsistSurgicalPatientCheck(): void {
    this.fetchConsistSurgicalPatientCheck().then((consistSurgicalPatientCheck) => {
      if (!isEmpty(consistSurgicalPatientCheck.message)) {
        this.showToast(consistSurgicalPatientCheck.message, ToastLevel.WARNING);
        return;
      }

      this.handlePatientRoomInfo();
    }).catch(() => {
      this.showToast(DisplayMessages.ENCOUNTER_NOT_FOUND, ToastLevel.WARNING);
    });
  }

  private async fetchConsistSurgicalPatientCheck(): Promise<ConsistSurgicalPatientCheckResponse> {
    const encounterId = this.context.get('encounterId');
    return await HttpService.get(Endpoint.CONSIST_SURGICAL_PATIENT_CHECK, { encounterId }).then((response) => response.body as ConsistSurgicalPatientCheckResponse);
  }

  private handlePatientRoomInfo(): void {
    this.fetchPatientRoomCheck().then((patientRoomInfo) => {
      if (isEmpty(patientRoomInfo.patientName)) {
        this.showToast(DisplayMessages.ENCOUNTER_NOT_FOUND, ToastLevel.WARNING);
        return;
      }

      this.handleBarcodeExpression(BarcodeExpressions.SURGICAL_ROOM);
      this.cardPanelAPI.setVisible(true);
      this.context.set({ patientRoomInfo });
      this.firstCheck = false;
    }).catch(() => {
      this.cardPanelAPI.setVisible(false);
      this.showToast(DisplayMessages.ENCOUNTER_NOT_FOUND, ToastLevel.WARNING);
    });
  }

  private async fetchPatientRoomCheck(): Promise<PatientRoomInfoResponse> {
    const encounterId = this.context.get('encounterId');
    return await HttpService.get(Endpoint.GET_PATIENT_ROOM_INFO, { encounterId }).then((response) => response.body as PatientRoomInfoResponse);
  }

  private handleSurgicalRoomCheck(): void {
    this.fetchSurgicalRoomCheck().then((surgicalRoomCheck) => {
      const checkSurgialRoom = surgicalRoomCheck.patientRoomCheckReturn === YesNoType.YES;
      const message = checkSurgialRoom ? DisplayMessages.PATIENT_CHECKED : DisplayMessages.SURGIAL_ROOM_NOT_MATCHS;
      const level = checkSurgialRoom ? ToastLevel.SUCCESS : ToastLevel.WARNING;

      if (checkSurgialRoom) {
        this.firstCheck = true;
        this.handleBarcodeExpression(BarcodeExpressions.WRISTBAND);
      }

      this.showToast(message, level);
    }).catch(() => {
      this.cardPanelAPI.setVisible(false);
      this.showToast(DisplayMessages.SURGIAL_ROOM_NOT_MATCHS, ToastLevel.WARNING);
    });
  }

  private handleBarcodeExpression(expression: string): void {
    this.barcodeAPI.setOpenReaderExpression(expression);
    this.barcodeAPI.setManualInputBarcodeExpression(expression);
  }

  private async fetchSurgicalRoomCheck(): Promise<SugicalRoomCkecResponse> {
    const encounterId = this.context.get('encounterId');
    const roomId = this.context.get('roomId');
    return await HttpService.get(Endpoint.SURGICAL_ROOM_CHECK, { encounterId, roomId }).then((response) => response.body as SugicalRoomCkecResponse);
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: message,
      level: level,
      message: message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });

  }
}
